// frontend/src/components/shared/CourseAvailabilityBadge.tsx
import { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';

export interface AvailabilityResult {
  is_offered?: boolean | null;
  typically_offered?: string[];
  [key: string]: unknown;
}

export interface CourseAvailabilityBadgeProps {
  courseCode: string;
  targetSemester: string;
  checkAvailability?: (courseCode: string, targetSemester: string) => Promise<AvailabilityResult>;
  showWhenAvailable?: boolean;
  compact?: boolean;
}

const CourseAvailabilityBadge = ({
  courseCode,
  targetSemester,
  checkAvailability,
  showWhenAvailable = false,
  compact = false,
}: CourseAvailabilityBadgeProps) => {
  const [availability, setAvailability] = useState<AvailabilityResult | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (courseCode && targetSemester && targetSemester !== 'bookmarks' && checkAvailability) {
      setLoading(true);
      checkAvailability(courseCode, targetSemester)
        .then((data) => {
          setAvailability(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Failed to check availability:', err);
          setLoading(false);
        });
    }
  }, [courseCode, targetSemester, checkAvailability]);

  if (
    loading ||
    !availability ||
    availability.is_offered === null ||
    availability.is_offered === undefined
  ) {
    return null;
  }

  const typicallyOffered = availability.typically_offered || [];
  const hasTypicalOfferings = typicallyOffered.length > 0;

  const formatTypicallyOffered = (): string => {
    if (!hasTypicalOfferings) return '';
    return typicallyOffered.join(', ');
  };

  if (availability.is_offered === true) {
    if (!showWhenAvailable || compact) {
      return null;
    }
    if (hasTypicalOfferings) {
      return (
        <span className="text-xs text-gray-600">
          Typically offered: {formatTypicallyOffered()}
        </span>
      );
    }
    return null;
  }

  const tooltipText = hasTypicalOfferings
    ? `Not offered this semester. Typically offered: ${formatTypicallyOffered()}`
    : `Not typically offered in this semester`;

  if (compact) {
    return (
      <span
        className="inline-flex items-center text-amber-700 bg-amber-100 p-1 rounded cursor-help"
        title={tooltipText}
      >
        <AlertCircle className="h-3 w-3" />
      </span>
    );
  }

  return (
    <div className="inline-flex flex-col gap-0.5">
      <span
        className="inline-flex items-center text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded cursor-help"
        title={tooltipText}
      >
        <AlertCircle className="h-3 w-3 mr-0.5" />
        Not offered
      </span>
      {hasTypicalOfferings && (
        <span className="text-xs text-gray-500">Typically: {formatTypicallyOffered()}</span>
      )}
    </div>
  );
};

export default CourseAvailabilityBadge;
